import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import RequireAuth from './auth/RequireAuth';
import SignIn from './pages/AuthPages/SignIn';
import AuthCallback from './pages/AuthPages/AuthCallback';
import NotFound from './pages/OtherPage/NotFound';
import Unauthorized from './pages/OtherPage/Unauthorized';
import AppLayout from './layout/AppLayout';
import { ScrollToTop } from './components/common/ScrollToTop';
import RouteFallback from './components/common/RouteFallback';
import { AuthAxiosProvider } from './auth/AuthAxiosProvider';
import { WorkflowTagsProvider } from './auth/WorkflowTagsProvider';
import AuthLoader from './components/common/AuthLoader';

const Home = lazy(() => import('./pages/Dashboard/Home.jsx'));
const UserProfiles = lazy(() => import('./pages/UserProfiles.jsx'));
const MyQcTaskPage = lazy(() => import('./pages/qc/my-task/MyQcTaskPage.jsx'));
const MyQcTaskReportsPage = lazy(
  () => import('./pages/qc/my-task/MyQcTaskReportsPage.jsx'),
);
const AssignedReviewQcTaskPage = lazy(
  () => import('./pages/qc/my-task/AssignedReviewQcTaskPage.jsx'),
);
const UnassignedTaskPage = lazy(
  () => import('./pages/qc/global-view/UnassignedTaskPage.jsx'),
);
const AssignedTaskPage = lazy(
  () => import('./pages/qc/global-view/AssignedTaskPage.jsx'),
);
const AssignedReviewTaskPage = lazy(
  () => import('./pages/qc/global-view/AssignedReviewTaskPage.jsx'),
);
const AnnotationSegmentationPage = lazy(
  () => import('./pages/qc/annotation-segmentation/AnnotationSegmentationPage.jsx'),
);

const Auth0Provider = lazy(() =>
  import('./auth/Auth0ProviderWithNavigate')
    .then((module) => ({ default: module.default }))
    .catch((err) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error('[App] Failed to load Auth0Provider chunk', err);
      }
      throw err;
    }),
);

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<AuthLoader />}>
        <Auth0Provider>
          {/* WorkflowTagsProvider must be *above* AuthAxiosProvider so child effects run first:
              AuthAxios attaches the Bearer interceptor before WorkflowTags' effect fires getGlobalWorkflows. */}
          <WorkflowTagsProvider>
            <AuthAxiosProvider>
              <ScrollToTop />
              <Routes>
                <Route path="/signin" element={<SignIn />} />
                <Route
                  path="/sigin"
                  element={<Navigate to="/signin" replace />}
                />
                <Route path="/callback" element={<AuthCallback />} />
                <Route path="/unauthorized" element={<Unauthorized />} />
                <Route path="/error-404" element={<NotFound />} />
                <Route
                  path="/signup"
                  element={<Navigate to="/signin" replace />}
                />

                <Route element={<RequireAuth />}>
                  <Route element={<AppLayout />}>
                    <Route index element={<Home />} />

                    <Route path="my-qc-task" element={<MyQcTaskPage />} />
                    <Route
                      path="my-qc-task-reports"
                      element={<MyQcTaskReportsPage />}
                    />
                    <Route
                      path="assigned-review-qc-task"
                      element={<AssignedReviewQcTaskPage />}
                    />

                    <Route
                      path="un-assigned-task"
                      element={<UnassignedTaskPage />}
                    />
                    <Route
                      path="assigned-task"
                      element={<AssignedTaskPage />}
                    />
                    <Route
                      path="review-task"
                      element={<AssignedReviewTaskPage />}
                    />

                    <Route
                      path="annotation-segmentation"
                      element={<AnnotationSegmentationPage />}
                    />

                    <Route path="profile" element={<UserProfiles />} />
                  </Route>
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
            </AuthAxiosProvider>
          </WorkflowTagsProvider>
        </Auth0Provider>
      </Suspense>
    </BrowserRouter>
  );
}
